const clearProjectionCaches = (project, { includeUnits = false } = {}) => {
  if (!project) return project;
  const nextProject = { ...project };
  [
    ...(includeUnits ? ["units"] : []),
    "cells",
    "steps",
    "substeps",
    "services",
  ].forEach((key) => {
    delete nextProject[key];
  });
  return nextProject;
};

const hasCurrentProjectionIdentity = (state, action) => (
  Number.isInteger(action.generation)
  && action.generation === state.projectionScopeGeneration
  && typeof action.workId === "string"
  && action.workId === state.activeProject?._id
  && typeof action.scopeKey === "string"
  && typeof action.requestKey === "string"
  && typeof action.requestId === "string"
);

const isCurrentProjectionScope = (state, action) => (
  hasCurrentProjectionIdentity(state, action) && action.scopeKey === state.projectionScopeKey
);

const isCurrentProjectionRequest = (state, action) => {
  if (
    !isCurrentProjectionScope(state, action)
  ) return false;
  const current = state.projectionLoads?.[action.requestKey];
  return current?.generation === action.generation
    && current?.workId === action.workId
    && current?.scopeKey === action.scopeKey
    && current?.requestId === action.requestId;
};

const PROJECTION_LOAD_ERROR = {
  code: "projection_load_failed",
  message: "Não foi possível carregar esta parte da projeção.",
};

const emptyGraphData = () => ({
  paidValues: [],
  toPayValues: [],
  disbursements: [],
  physicals: [],
  predicted: [],
  baseline: [],
  measured: [],
  scheduleCurves: [],
  scheduleSource: null,
  takenTo: { byItem: [], byMonth: [] },
  takenFrom: { byItem: [], byMonth: [] },
});

const updateProjectionLoad = (state, action, load) => ({
  ...state,
  projectionLoads: {
    ...(state.projectionLoads ?? {}),
    [action.requestKey]: {
      generation: action.generation,
      workId: action.workId,
      scopeKey: action.scopeKey,
      requestId: action.requestId,
      ...load,
    },
  },
});

const completeProjectionLoad = (state, action, activeProject) => updateProjectionLoad({
  ...state,
  activeProject,
}, action, { status: "success", error: null });

export default (state, action) => {
  switch (action.type) {
    case "FOUND_PROJECTS":
      return {
        ...state,
        projects: action.data.projects,
        companies: action.data.companies
      }
    case "SELECT_PROJECT":
      return {
        ...state,
        activeProject: action.data,
        projectionScopeGeneration: (state.projectionScopeGeneration ?? 0) + 1,
        projectionScopeKey: null,
        projectionLoads: {},
      }
    case "UPDATE_JOB_STATUS":
      return {
        ...state,
        updateJobsByBuilding: {
          ...state.updateJobsByBuilding,
          [action.data.buildingId]: action.data.job,
        },
      }
    case "UPDATE_FOLLOW_UPS": { // Remove séries antigas quando o acesso fecha, sem fabricar zeros.
      const restricted = action.data.access?.receivables === "restricted";
      const currentProject = { ...state.activeProject };
      if (restricted) {
        delete currentProject.toReceiveBills;
        delete currentProject.receivedBills;
        delete currentProject.allBalances;
      }
      const receivables = restricted ? {} : {
        toReceiveBills: action.data.toReceiveBills,
        receivedBills: action.data.receivedBills,
        allBalances: action.data.allBalances,
      };
      return {
        ...state,
        activeProject: {
          ...currentProject,
          followUps: action.data.followUps,
          paidValues: action.data.paidValues,
          disbursements: action.data.disbursements,
          toPayValues: action.data.toPayValues,
          takenTo: action.data.takenTo,
          takenFrom: action.data.takenFrom,
          accumulatedDisbursement: action.data.accumulatedDisbursement,
          accumulatedPaid: action.data.accumulatedPaid,
          physicals: action.data.physicals,
          metrics: action.data.metrics
            ? { ...(currentProject.metrics ?? {}), ...action.data.metrics }
            : currentProject.metrics,
          scheduleSource: action.data.scheduleSource,
          access: {
            ...(state.activeProject.access ?? {}),
            ...(action.data.access ?? {}),
          },
          ...receivables,
        }
      }
    }
    case "CHANGE_FILTER_DATES":
      return {
        ...state,
        activeProject: clearProjectionCaches(state.activeProject),
        projectionScopeGeneration: (state.projectionScopeGeneration ?? 0) + 1,
        projectionScopeKey: null,
        projectionLoads: {},
        filterDates: {
          beginAt: action.data.begin,
          endAt: action.data.end,
        }
      }
    case "CHANGE_GRAPH_FILTER_DATES":
      return {
        ...state,
        offCanvasFilterDates: {
          beginAt: action.data.begin,
          endAt: action.data.end,
        }
      }
    case "PROJECTION_SCOPE_STARTED":
      if (!hasCurrentProjectionIdentity(state, action) || action.scopeKey === state.projectionScopeKey) return state;
      return {
        ...state,
        projectionScopeKey: action.scopeKey,
        projectionLoads: {},
        activeProject: clearProjectionCaches(state.activeProject, { includeUnits: true }),
      };
    case "PROJECTION_LOAD_STARTED":
      if (
        !isCurrentProjectionScope(state, action)
        || typeof action.requestKey !== "string"
        || typeof action.requestId !== "string"
      ) return state;
      return updateProjectionLoad(state, action, { status: "loading", error: null });
    case "PROJECTION_LOAD_FAILED":
      if (!isCurrentProjectionRequest(state, action)) return state;
      return updateProjectionLoad(state, action, { status: "error", error: PROJECTION_LOAD_ERROR });
    case "LOADED_UNITS":
      if (!isCurrentProjectionRequest(state, action)) return state;
      return completeProjectionLoad(state, action, {
        ...state.activeProject,
        units: action.data.units
      });
    case "LOADED_CELLS":
      if (!isCurrentProjectionRequest(state, action)) return state;
      return completeProjectionLoad(state, action, {
        ...state.activeProject,
        cells: {
          ...(state.activeProject.cells ?? {}),
          [action.data.wbs_code]: action.data.cells
        }
      });
    case "LOADED_STEPS":
      if (!isCurrentProjectionRequest(state, action)) return state;
      return completeProjectionLoad(state, action, {
        ...state.activeProject,
        steps: {
          ...(state.activeProject.steps ?? {}),
          [action.data.wbs_code]: action.data.steps
        }
      });
    case "LOADED_SUBSTEPS":
      if (!isCurrentProjectionRequest(state, action)) return state;
      return completeProjectionLoad(state, action, {
        ...state.activeProject,
        substeps: {
          ...(state.activeProject.substeps ?? {}),
          [action.data.wbs_code]: action.data.substeps
        }
      });
    case "LOADED_SERVICES":
      if (!isCurrentProjectionRequest(state, action)) return state;
      return completeProjectionLoad(state, action, {
        ...state.activeProject,
        services: {
          ...(state.activeProject.services ?? {}),
          [action.data.wbs_code]: action.data.services
        }
      });
    case "SELECTED_DETAIL":
      return {
        ...state,
        selectedDetail: action.data,
        graphData: emptyGraphData(),
        graphRequestKey: null,
        scheduleAnalysis: {
          requestKey: null,
          loading: false,
          error: null,
          data: null,
        },
      }
    case "UPDATE_CURVE_ID":
      return {
        ...state,
        curveID: action.data
      }
    case "UPDATE_RESOURCE_EAP":
      return {
        ...state,
        resourceEAP: action.data,
      }
    case "UPDATE_CUB_BASED":
      return {
        ...state,
        cubBased: action.data,
      }
    case "UPDATE_FORECAST_BASED":
      return {
        ...state,
        forecastBased: action.data,
      }
    case "SELECT_XLSX_DATA": {
      return {
        ...state,
        xlsxData: action.data,
      };
    }
    case "SELECT_GRAPH": {
      return {
        ...state,
        selectedGraph: action.data,
      };
    }
    case "FILTER_DOCS": {
      return {
        ...state,
        filterDocs: action.data,
      };
    }
    case "GRAPH_DATA_START": {
      return {
        ...state,
        graphRequestKey: action.requestKey,
      };
    }
    case "UPDATE_GRAPH_DATA": {
      if (
        action.requestKey
        && state.graphRequestKey !== action.requestKey
      ) return state;
      return {
        ...state,
        graphData: action.data,
        graphRequestKey: action.requestKey ?? state.graphRequestKey,
      };
    }
    case "SCHEDULE_ANALYSIS_START": {
      return {
        ...state,
        scheduleAnalysis: {
          requestKey: action.requestKey,
          loading: true,
          error: null,
          data: null,
        },
      };
    }
    case "SCHEDULE_ANALYSIS_SUCCESS": {
      if (state.scheduleAnalysis?.requestKey !== action.requestKey) return state;
      return {
        ...state,
        scheduleAnalysis: {
          requestKey: action.requestKey,
          loading: false,
          error: null,
          data: action.data,
        },
      };
    }
    case "SCHEDULE_ANALYSIS_FAILURE": {
      if (state.scheduleAnalysis?.requestKey !== action.requestKey) return state;
      return {
        ...state,
        scheduleAnalysis: {
          requestKey: action.requestKey,
          loading: false,
          error: action.error,
          data: null,
        },
      };
    }
    case "UPDATE_GRAPH_MAX_DATES": {
      return {
        ...state,
        graphMaxDates: {
          end: action.data.end,
          begin: action.data.begin
        }
      };
    }
    case "UPDATE_ADVANCED_ITEMS": {
      return {
        ...state,
        advancedItems: action.data
      };
    }
    case "UPDATE_PROJECTION_TABLE_VIEW": {
      return {
        ...state,
        projectionTable: action.data
      };
    }
    case "UPDATE_PROJECTION_TABLE_DATE": {
      return {
        ...state,
        tableMonthSelected: action.data
      };
    }
    default:
      return state;
  }
};
